import { Inject, Injectable } from "@angular/core";
import { Observable, Subject, combineLatest, of } from "rxjs";
import { AppMetaData, ApplicantAcsWrittenResultTypeAllItems, CheckRideRequestEntity, CheckRideRequestQueryBuilder, CheckRideRequestStatusAllItems, CheckRideRequestSubStatusAllItems, CheckrideRequestAggregateBaseQueryBuilder, CheckrideRequestAggregateBaseQueryMetaDataAttributes, ReportingACSMissedCodesAggregateQueryBuilder, ReportingACSMissedCodesAggregateQueryEntity } from "../../../meta-data/app-meta-data.service";
import { ChartAdditionalFilterOptions, DwField, DwFieldAggregate, DwFieldType, DwItemAttribute, DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken, DwQuery, DwQueryBuilderGen, DwQueryFilterGroup, DwQueryHelper, DwQueryMetaData, DwTemplateUtilService, deepCopy, fieldSelectorType } from "@devwareapps/devware-cap";
import { map, mergeMap } from "rxjs/operators";
import { filterFieldSelectorType } from "@devwareapps/devware-cap/devware-orm/query/builders/dw-query-filter-builder-generic.model";
import { DwChartPreferences, DwChartSeries, DwChartTypes } from "../../z-devware/visualizations/models/dw-chart-preferences.model";

import { DwChartDataSelectedEvent } from "../../z-devware/visualizations/models/dw-chart-data-selected-event.model";
import { DwChartColors } from "../../z-devware/visualizations/models/dw-chart-colors.model";
import { DwTileConfig } from "../../z-devware/visualizations/models/dw-tile-config.model";
import { DwMdTemplateService } from "@devwareapps/devware-cap/devware-ui-metadata/controls/templates-controls/services/dw-md-template-service";
import { ChartFilters } from "../../financials/models/chart-filters.model";
import { BaseQueryResult } from "../../financials/models/base-query-result.model";

@Injectable({
    providedIn: 'root'
})
export class ReportsRepositoryService {

    static closeGridSubject = new Subject<void>();

    constructor(@Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        private dwTemplateUtil: DwTemplateUtilService,
    ) {

    }

    // Close the grid drill-down
    closeGrid() {
        ReportsRepositoryService.closeGridSubject.next();
    }

    // Observable for when the grid should be closed, such as when navigating to a different report
    getCloseGridObservable(): Observable<void> {
        return ReportsRepositoryService.closeGridSubject.asObservable();
    }

    public GetMissedAcsCodes(filters: DwQueryFilterGroup | any): Observable<ReportingACSMissedCodesAggregateQueryEntity[]> {
        return this.GetMissedAcsCodeQuery(filters)
            .pipe(mergeMap(query => 
                this.dwOrmDataService.executeQuery(query)
            ));
    }

    public GetMissedAcsCodeQuery(filters: DwQueryFilterGroup | any): Observable<ReportingACSMissedCodesAggregateQueryBuilder> {

        return this.buildMissedAcsCodeAggregateBaseQuery(filters)
            .pipe(map(result => {
                const query = result.queryBuilder;

                query.addField(f => f.AcsCode);
                query.addField(f => f.AreaOfOperationName);
                query.addField(f => f.TaskName);
                query.addField(f => f.ApplicantAcsWrittenResultTypeId);

                query.addOrderByDesc(f => f.MissedCodeCount);

                return query;
            }));
    }

    // private getFinancialSummaryQuery(filters: ChartFilters, additionalFilterOptions: ChartAdditionalFilterOptions): Observable<DwQuery> {
    //     return this.buildCheckRideBaseQuery(filters, additionalFilterOptions)
    //         .pipe(map(result => {
    //             const query = result.queryBuilder;

    //             query.query.FieldSettings.AggregateQueryWithRollup = true;

    //             query.addField(f => f.AggrScheduledDateTimeYear);

    //             query.addOrderBy(f => f.AggrScheduledDateTimeYear);

    //             return query.query;
    //         }));
    // }

    private buildMissedAcsCodeAggregateBaseQuery(filters?: DwQueryFilterGroup | any): Observable<{queryMetaData: DwQueryMetaData, queryBuilder: ReportingACSMissedCodesAggregateQueryBuilder}> {

        return AppMetaData.Queries.ReportingACSMissedCodesAggregate.CreateQueryBuilderWithMetaData(this.dwMetaDataService)
            .pipe(map(qmd => {
                return {
                    queryBuilder: this.setupMissedAcsCodeAggregateBaseQuery(qmd.queryBuilder, filters),
                    queryMetaData: qmd.queryMetaData
                }
            }));
    }

    setupMissedAcsCodeAggregateBaseQuery(query: ReportingACSMissedCodesAggregateQueryBuilder, filters: DwQueryFilterGroup | any): ReportingACSMissedCodesAggregateQueryBuilder {
        query.query = Object.assign(new DwQuery(query.query.RootTable), deepCopy(query.query));

        const qh = new DwQueryHelper();

        // Safely add the filters to the query
        query.query = qh.convertExistingQuery(query.query, filters);

        query.query.FieldSettings.AggregateQuery = true;
        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        // Clear all fields so we can create a clean aggregate query - this allows us to filter on any fields, but add only the fields we want to group by
        var originalFields = qh.clearAllFields(query.query);

        for (let field of originalFields) {
            if (field.AggregateFunction != DwFieldAggregate.None) {
                // Re-add any existing aggregate fields since those are needed for the drilldown queries
                query.query.AddField(field);
            }
        }

        query.query.Order = [];

        return query;
    }

    formatNumeric(value: any, decimals: number, money: boolean, excludeLocalNumericFormatting: boolean): string {
        const numberValue = parseFloat(value);

        if (isNaN(numberValue)) {
            return null;
        }

        let formattedValue = numberValue.toFixed(decimals);

        if (!excludeLocalNumericFormatting) {
            formattedValue = numberValue.toLocaleString('en-us', { maximumFractionDigits: decimals, minimumFractionDigits: decimals });
        }

        if (money) {
            formattedValue = `$${formattedValue}`;
        }

        return formattedValue;
    }

    colors = DwChartColors;

    // ============ ACS Trends Query Methods ============

    /**
     * Pattern 1: Get missed ACS codes ranked by occurrence
     * @param filters Custom filter group for filtering results
     * @param topN Number of top results to return (default: 20)
     */
    public GetMissedAcsCodesByRanking(filters: DwQueryFilterGroup | any, topN: number = 20): Observable<{ data: any[]; query: DwQuery }> {
        return this.buildMissedAcsCodeAggregateBaseQuery(filters)
            .pipe(mergeMap(result => {
                const query = result.queryBuilder;

                // Group by ACS Code
                query.addField(f => f.AcsCodeId);
                query.addField(f => f.AcsCode);
                query.addField(f => f.AcsTestId);
                query.addField(f => f.AreaOfOperationName);
                query.addField(f => f.TaskName);

                // Rank by most-missed codes first
                query.addOrderByDesc(f => f.MissedCodeCount);

                // Apply paging/limit to match previous behavior
                if (typeof query.setupPaging === 'function') {
                    query.setupPaging(1, topN);
                }

                return this.dwOrmDataService.executeQuery(query)
                    .pipe(map(data => ({ data: data, query: query.query })));
            }));
    }

    /**
     * Pattern 2: Get missed ACS codes grouped by Written vs Exam test type
     * @param filters Custom filter group for filtering results
     * @param topN Number of top codes to return (default: 15)
     */
    public GetMissedAcsCodesByWrittenVsExam(filters: DwQueryFilterGroup | any, topN: number = 15): Observable<{ data: any[]; gridData: any[]; query: DwQuery }> {
        const rawRowLimit = topN * 2;

        return this.buildMissedAcsCodeAggregateBaseQuery(filters)
            .pipe(mergeMap(result => {
                const query = result.queryBuilder;

                // Group by ACS Code
                query.addField(f => f.AcsCodeId);
                query.addField(f => f.AcsCode);
                query.addField(f => f.AcsTestId);
                query.addField(f => f.AreaOfOperationName);
                query.addField(f => f.TaskName);
                query.addField(f => f.ApplicantAcsWrittenResultTypeId);
                //query.addField(f => f.MissedCodeCount);

                // Order by total count descending
                query.addOrderByDesc(f => f.MissedCodeCount);

                query.setupPaging(1, rawRowLimit);

                return this.dwOrmDataService.executeQuery(query)
                    .pipe(map(data => ({ data: data, query: query.query })));
            }),
            map(result => ({
                data: this.pivotByTestType(result.data, topN),
                gridData: result.data,
                query: result.query
            })));
    }

    /**
     * Pattern 3: Get missed ACS codes grouped by Area of Operation and Task
     * @param filters Custom filter group for filtering results
     */
    public GetMissedAcsCodesByAreaTask(filters: DwQueryFilterGroup | any): Observable<{ data: any[]; query: DwQuery }> {
        return this.buildMissedAcsCodeAggregateBaseQuery(filters)
            .pipe(mergeMap(result => {
                const query = result.queryBuilder;

                // Group by Area and Task
                query.addField(f => f.AcsAreaOfOperationId);
                query.addField(f => f.AreaOfOperationName);
                query.addField(f => f.AcsAreaOfOperationTaskId);
                query.addField(f => f.TaskName);
                //query.addField(f => f.MissedCodeCount);

                // Order by Area, then Task
                query.addOrderBy(f => f.AreaOfOperationName);
                query.addOrderBy(f => f.TaskName);

                return this.dwOrmDataService.executeQuery(query)
                    .pipe(map(data => ({ data: data, query: query.query })));
            }));
    }

    /**
     * Pattern 4: Get missed ACS codes over time periods
     * @param filters Custom filter group for filtering results
     */
    public GetMissedAcsCodesByTimePeriod(filters: DwQueryFilterGroup | any): Observable<{ data: any[]; query: DwQuery }> {
        return this.buildMissedAcsCodeAggregateBaseQuery(filters)
            .pipe(mergeMap(result => {
                const query = result.queryBuilder;

                // Group by date - we'll use CheckrideDateTime directly
                query.addField(f => f.CheckrideDateTime);
                //query.addField(f => f.MissedCodeCount);

                // Order by date
                query.addOrderBy(f => f.CheckrideDateTime);

                return this.dwOrmDataService.executeQuery(query)
                    .pipe(map(data => ({ data: data, query: query.query })));
            }),
            map(result => ({
                data: this.aggregateByPeriod(result.data),
                query: result.query
            })));
    }

    public GetMissedAcsCodesConfigurableGrid(filters: DwQueryFilterGroup | any, selectedDimensions: string[]): Observable<{ data: any[]; query: DwQuery }> {
        return this.buildMissedAcsCodeAggregateBaseQuery(filters)
            .pipe(mergeMap(result => {
                const query = result.queryBuilder;

                for (const dimension of selectedDimensions || []) {
                    this.addConfigurableGridDimension(query, dimension);
                }

                if ((selectedDimensions || []).length === 0) {
                    query.addOrderByDesc(f => f.MissedCodeCount);
                }

                query.query.FieldSettings.IncludeFields = (query.query.FieldSettings.IncludeFields || [])
                    .filter(field => !!field);
                query.query.Order = (query.query.Order || [])
                    .filter(orderField => !!orderField);

                return this.dwOrmDataService.executeQuery(query)
                    .pipe(map(data => ({ data: data, query: query.query })));
            }));
    }

    /**
     * Get detailed records for drill-down
     * @param filters Filters including the selected data point
     */
    public GetMissedAcsCodesDetail(filters: DwQueryFilterGroup | any): Observable<any[]> {
        return this.buildMissedAcsCodeAggregateBaseQuery(filters)
            .pipe(mergeMap(result => {
                const query = result.queryBuilder;

                // Don't aggregate - get actual records
                query.query.FieldSettings.AggregateQuery = false;

                // Add all detail fields
                query.addField(f => f.AssessmentId);
                query.addField(f => f.AcsCode);
                query.addField(f => f.AreaOfOperationName);
                query.addField(f => f.TaskName);
                query.addField(f => f.ApplicantName);
                query.addField(f => f.ExaminerName);
                query.addField(f => f.CheckrideDateTime);
                query.addField(f => f.CheckrideTypeId);
                query.addField(f => f.ApplicantAcsWrittenResultTypeId);

                query.addOrderByDesc(f => f.CheckrideDateTime);

                return this.dwOrmDataService.executeQuery(query);
            }));
    }

    // ============ Helper Methods for ACS Trends ============

    /**
     * Pivots data by test type (Written vs Exam)
     */
    private pivotByTestType(data: any[], topN: number): any[] {
        const codeMap = new Map<string, any>();
        const countField = AppMetaData.Queries.ReportingACSMissedCodesAggregate.Attributes.MissedCodeCount.fieldName;

        data.forEach(item => {
            const code = item.AcsCode;
            if (!codeMap.has(code)) {
                codeMap.set(code, {
                    AcsCode: code,
                    AcsCodeId: item.AcsCodeId,
                    WrittenMissedCount: 0,
                    ExamMissedCount: 0,
                    TotalMissedCount: 0
                });
            }

            const codeData = codeMap.get(code);
            const count = item[countField] || 0;
            
            if (item.ApplicantAcsWrittenResultTypeId === ApplicantAcsWrittenResultTypeAllItems.Written) {
                codeData.WrittenMissedCount += count;
            } else if (item.ApplicantAcsWrittenResultTypeId === ApplicantAcsWrittenResultTypeAllItems.Exam) {
                codeData.ExamMissedCount += count;
            }
            
            codeData.TotalMissedCount += count;
        });

        // Convert to array and sort by total
        const result = Array.from(codeMap.values())
            .sort((a, b) => b.TotalMissedCount - a.TotalMissedCount)
            .slice(0, topN);

        return result;
    }

    /**
     * Formats year/month into a display period
     */
    private formatPeriod(year: number, month: number): string {
        if (!year) {
            return 'Unknown';
        }

        if (!month) {
            return year.toString();
        }

        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[month - 1]} ${year}`;
    }

    /**
     * Formats a date string to year-month period
     */
    private formatPeriodFromDate(dateString: string): string {
        if (!dateString) {
            return 'Unknown';
        }

        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;

        return this.formatPeriod(year, month);
    }

    /**
     * Consolidates raw date-grouped aggregate rows into month buckets for charting.
     */
    private aggregateByPeriod(data: any[]): any[] {
        const countField = AppMetaData.Queries.ReportingACSMissedCodesAggregate.Attributes.MissedCodeCount.fieldName;
        const periodMap = new Map<string, any>();

        data.forEach(item => {
            const dateString = item.CheckrideDateTime;
            const period = this.formatPeriodFromDate(dateString);
            const periodDate = dateString ? new Date(dateString) : null;
            const sortValue = periodDate ? (periodDate.getFullYear() * 100) + (periodDate.getMonth() + 1) : Number.MAX_SAFE_INTEGER;
            const count = item[countField] || 0;

            if (!periodMap.has(period)) {
                periodMap.set(period, {
                    Period: period,
                    PeriodSortValue: sortValue,
                    [countField]: 0
                });
            }

            const periodData = periodMap.get(period);
            periodData[countField] += count;
        });

        return Array.from(periodMap.values())
            .sort((a, b) => a.PeriodSortValue - b.PeriodSortValue);
    }

    private addConfigurableGridDimension(query: ReportingACSMissedCodesAggregateQueryBuilder, dimension: string): void {
        const attrs = AppMetaData.RoAssessmentAcsMissedCodeFact.Attributes;

        switch (dimension) {
            case 'examiner':
                query.addField(f => f.FlightSchoolExaminerId);
                query.addOrderBy(f => f.FlightSchoolExaminerId);
                break;
            case 'checkrideType':
                query.addField(f => f.CheckrideTypeId);
                query.addOrderBy(f => f.CheckrideTypeId);
                break;
            case 'attemptClassification':
                query.query.AddField(attrs.CheckRideTestAttemptClassificationId.fieldNameWithItemPrefix);
                query.query.AddField(attrs.AttemptClassification.fieldNameWithItemPrefix);
                query.query.OrderBy(attrs.AttemptClassification.fieldNameWithItemPrefix);
                break;
            case 'testType':
                query.addField(f => f.ApplicantAcsWrittenResultTypeId);
                query.addOrderBy(f => f.ApplicantAcsWrittenResultTypeId);
                break;
            case 'flightSchool':
                query.addField(f => f.FlightSchoolId);
                query.addOrderBy(f => f.FlightSchoolId);
                break;
            case 'flightInstructor':
                query.addField(f => f.FlightSchoolInstructorId);
                query.addOrderBy(f => f.FlightSchoolInstructorId);
                break;
            case 'acsTest':
                query.addField(f => f.AcsTestId);
                query.addOrderBy(f => f.AcsTestId);
                break;
        }
    }
}  
